import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Chip } from '@mui/material';
import { PageLayout } from '../../PageLayout';
import { EngagementEditorProvider, SideBySideView } from '../../Compounds/Advise/SideBySideView';
import {
  AdviseEngagement,
  AdviseSupplier,
  AdviseCustomer,
  EngagementConfiguration,
  DiscoveryFormSection,
  MatrixCriterion
} from '../../../types/advise';
import { validate as isUuid } from 'uuid';
import { callExternalApi } from '../../../Api/callExternalApi';
import { extractApiErrorMessage } from '../../../utils/extractApiErrorMessage';

interface SharedEngagementData {
  engagement: AdviseEngagement;
  suppliers: AdviseSupplier[];
  customer: AdviseCustomer | null;
  configuration?: EngagementConfiguration;
  discovery?: { sections: DiscoveryFormSection[] };
  functionality_matrix?: { criteria: MatrixCriterion[] };
}

const ignoreEdit = () => {};

export const AdviseSharedEngagementPage = () => {
  const [searchParams] = useSearchParams();
  const engagementId = searchParams.get('id');
  const token = searchParams.get('token');

  const [data, setData] = useState<SharedEngagementData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Engagement ids are UUIDs; refusing anything else keeps a dot segment from stepping out of the engagement's path.
    if (!engagementId || !isUuid(engagementId) || !token) {
      setError('Invalid share link. It needs a valid engagement ID and a token.');
      setLoading(false);
      return;
    }

    const fetchSharedData = async () => {
      setLoading(true);
      setError(null);

      const url = `api/advise/engagements/${engagementId}/shared?id=${engagementId}&token=${encodeURIComponent(token)}`;

      const result = await callExternalApi<SharedEngagementData>(
        {
          url,
          method: 'GET',
          headers: { 'content-type': 'application/json' }
        },
        true
      );

      if (result.error) {
        setError(extractApiErrorMessage(result.error, 'Unable to load shared engagement.'));
      } else if (result.data) {
        setData(result.data);
      }
      setLoading(false);
    };

    fetchSharedData();
  }, [engagementId, token]);

  const selectedSuppliers = useMemo(
    () => data?.suppliers?.filter((v) => data.engagement?.selected_suppliers?.includes(v.id)) || [],
    [data]
  );

  if (loading) {
    return (
      <Box className={'min-h-screen flex items-center justify-center bg-gray-50'}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box className={'min-h-screen flex items-center justify-center bg-gray-50'}>
        <Alert
          severity="error"
          sx={{ maxWidth: 500 }}>
          {error || 'Engagement not found or access denied.'}
        </Alert>
      </Box>
    );
  }

  const { engagement, suppliers, customer, configuration, discovery, functionality_matrix } = data;

  return (
    <PageLayout
      pageTitle={engagement.engagement_name || 'Shared Engagement'}
      pageActions={
        <Chip
          label="Read Only"
          size="small"
          variant="outlined"
        />
      }
      dataCy="advise-shared-engagement-page">
      <Box className="flex items-center gap-4 mt-2 mb-4">
        {customer && (
          <Typography
            variant="body2"
            color="textSecondary">
            Customer: {customer.company_name}
          </Typography>
        )}
        <Typography
          variant="body2"
          color="textSecondary">
          {selectedSuppliers.length} supplier
          {selectedSuppliers.length !== 1 ? 's' : ''}
        </Typography>
        {engagement.owner_name && (
          <Typography
            variant="body2"
            color="textSecondary">
            Shared by: {engagement.owner_name}
          </Typography>
        )}
      </Box>

      {/* Wide tables scroll inside the page, so it never grows wider than the header. */}
      <Box className="overflow-x-auto">
        <EngagementEditorProvider
          value={{
            engagement,
            suppliers,
            canEdit: false,
            editEngagement: ignoreEdit,
            discovery: { sections: discovery?.sections ?? [], answer: ignoreEdit },
            functionalityMatrix: {
              criteria: functionality_matrix?.criteria ?? [],
              updateCriterion: ignoreEdit,
              selectFeature: ignoreEdit
            }
          }}>
          <SideBySideView
            steps={configuration?.steps}
            shareLink
          />
        </EngagementEditorProvider>
      </Box>
    </PageLayout>
  );
};
